import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Pin,
  Plus,
  Sparkles,
  X as XIcon,
  ChevronRight,
  Loader2,
  ArrowUpRight,
  Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * TopPicksBar — pinned priority section at the top of a list.
 * Renders linked tasks and free-text quick adds; AI suggests up to 3 candidates.
 */
export default function TopPicksBar({ listId, readOnly, tasks, onOpenTask, onTaskChanged }) {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false); // in-flight mutation guard (prevents dup submits)
  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const inputRef = useRef(null);
  // Refs guard against double-fire (StrictMode dev re-invocation, fast double-click, race conditions).
  const submittingRef = useRef(false);
  const pickedTaskIdsRef = useRef(new Set());

  const load = useCallback(async () => {
    if (!listId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/lists/${listId}/top-picks`);
      setPicks(data);
    } catch {
      /* silent — list might just have none */
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    load();
    setSuggestions([]);
    pickedTaskIdsRef.current = new Set();
  }, [load]);

  // Restore focus to the quick-add input after a pick is added.
  // setTimeout(0) inside addFree() races with the browser's post-form-submit
  // focus transition (submit button briefly steals focus). Watching picks.length
  // ensures we refocus *after* the React commit, via rAF for safe ordering.
  const picksLen = picks.length;
  const prevPicksLen = useRef(picksLen);
  useEffect(() => {
    if (adding && picksLen > prevPicksLen.current) {
      const raf = requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      prevPicksLen.current = picksLen;
      return () => cancelAnimationFrame(raf);
    }
    prevPicksLen.current = picksLen;
  }, [picksLen, adding]);

  // Helper: append a pick only if its id isn't already present (defensive dedup).
  const appendPickUnique = (newPick) =>
    setPicks((p) => (p.some((x) => x.id === newPick.id) ? p : [...p, newPick]));

  const addFree = async (label) => {
    if (submittingRef.current || busy) return;
    const text = (label || "").trim();
    if (!text) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/lists/${listId}/top-picks`, { kind: "free", label: text });
      appendPickUnique(data);
      setDraft("");
      // Focus is restored via the useEffect watching picks.length below.
      // (setTimeout(0) races with the browser's post-submit focus transition.)
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add");
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const addTaskPick = async (task_id) => {
    if (submittingRef.current || busy) return;
    if (pickedTaskIdsRef.current.has(task_id)) return; // already in-flight or picked
    if (picks.some((p) => p.kind === "task" && p.task_id === task_id)) return;
    pickedTaskIdsRef.current.add(task_id);
    submittingRef.current = true;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/lists/${listId}/top-picks`, { kind: "task", task_id });
      appendPickUnique(data);
      setSuggestions((s) => s.filter((x) => x.task_id !== task_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't pin");
    } finally {
      submittingRef.current = false;
      setBusy(false);
      // Keep the guard set so the same task can't be re-pinned via stale UI
    }
  };

  const remove = async (pickId) => {
    try {
      await axios.delete(`${API}/lists/${listId}/top-picks/${pickId}`);
      setPicks((p) => p.filter((x) => x.id !== pickId));
    } catch {
      toast.error("Couldn't remove");
    }
  };

  // Toggle pick completion. For free picks, store flag locally on the pick.
  // For task picks, the backend delegates to the linked task — so we also need
  // to bubble the change up so the task list re-renders / removes the row.
  const toggleComplete = async (pick) => {
    const linkedTask = pick.kind === "task" ? (tasks || []).find((t) => t.id === pick.task_id) : null;
    // Resolve current completed state robustly: prefer the live task, fall back
    // to the pick's own flag (kept in sync after each toggle below). This avoids
    // the "!undefined === true" bug when the linked task isn't yet in the
    // tasks prop (e.g. during the parent's refetch window).
    const currentCompleted = pick.kind === "task"
      ? (linkedTask ? !!linkedTask.completed : !!pick.completed)
      : !!pick.completed;
    const nextCompleted = !currentCompleted;
    // Optimistic UI — for BOTH kinds. For task picks, mirror onto pick.completed
    // so the checkbox stays consistent until fetchTasks resolves.
    setPicks((arr) => arr.map((x) => (x.id === pick.id ? { ...x, completed: nextCompleted } : x)));
    try {
      await axios.patch(`${API}/lists/${listId}/top-picks/${pick.id}`, { completed: nextCompleted });
      // For task picks, ask the parent to re-fetch tasks so the strike-through reflects
      if (pick.kind === "task") onTaskChanged?.();
    } catch {
      // Rollback
      setPicks((arr) => arr.map((x) => (x.id === pick.id ? { ...x, completed: currentCompleted } : x)));
      toast.error("Couldn't update");
    }
  };

  const promote = async (pickId) => {
    try {
      await axios.post(`${API}/lists/${listId}/top-picks/${pickId}/promote`);
      toast.success("Added as a task");
      // Refresh both picks and (via parent reload) tasks
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't promote");
    }
  };

  const aiSuggest = async () => {
    setSuggestBusy(true);
    try {
      const { data } = await axios.get(`${API}/lists/${listId}/top-picks/suggest`);
      setSuggestions(data.suggestions || []);
      if ((data.suggestions || []).length === 0) {
        toast.info(data.reason || "Nothing to suggest right now.");
      }
    } catch {
      toast.error("AI suggest failed");
    } finally {
      setSuggestBusy(false);
    }
  };

  if (!listId || loading) return null;
  const hasPicks = picks.length > 0;

  return (
    <section className="mb-6 border border-zinc-200 bg-gradient-to-r from-amber-50/60 to-orange-50/40" data-testid="top-picks-bar">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <Pin className="w-3.5 h-3.5 text-amber-700 rotate-45" strokeWidth={2} />
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-700">
            Top picks · {picks.length}
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={aiSuggest}
              disabled={suggestBusy}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-medium text-[#002FA7] hover:text-zinc-900 px-2 py-1 disabled:opacity-50"
              data-testid="top-picks-ai-suggest"
              title="AI suggests 3 picks from this list"
            >
              <Sparkles className={`w-3 h-3 ${suggestBusy ? "animate-pulse" : ""}`} strokeWidth={1.75} />
              {suggestBusy ? "…" : "AI suggest"}
            </button>
            <button
              type="button"
              onClick={() => setAdding((a) => !a)}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-medium text-zinc-700 hover:text-zinc-900 px-2 py-1"
              data-testid="top-picks-add-btn"
            >
              <Plus className="w-3 h-3" strokeWidth={2} /> {adding ? "Cancel" : "Add"}
            </button>
          </div>
        )}
      </div>

      {/* Suggestion list */}
      {suggestions.length > 0 && (
        <div className="border-b border-zinc-200 bg-white/60 px-4 py-2 space-y-1" data-testid="top-picks-suggestions">
          <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 mb-1">AI suggestions — click to pin</p>
          {suggestions.map((s) => (
            <button
              type="button"
              key={s.task_id}
              onClick={() => addTaskPick(s.task_id)}
              disabled={busy}
              className="w-full flex items-center justify-between gap-2 text-left px-2 py-1.5 hover:bg-white border border-transparent hover:border-zinc-200 disabled:opacity-50"
              data-testid={`top-picks-suggestion-${s.task_id}`}
            >
              <span className="text-xs text-zinc-700 truncate">
                <span className="text-[9px] uppercase tracking-[0.1em] text-zinc-400 mr-1.5">{s.priority}</span>
                {s.title}
              </span>
              <Plus className="w-3 h-3 text-zinc-500 shrink-0" strokeWidth={2} />
            </button>
          ))}
        </div>
      )}

      {/* Add form */}
      {adding && (
        <form
          onSubmit={(e) => { e.preventDefault(); addFree(draft); }}
          className="border-b border-zinc-200 bg-white/60 px-4 py-2 flex items-center gap-2"
          data-testid="top-picks-add-form"
        >
          <input
            ref={inputRef}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                setDraft("");
                setAdding(false);
              }
            }}
            placeholder="Quick add… (Enter to add, Esc to close)"
            maxLength={120}
            disabled={busy}
            className="flex-1 h-8 px-2 text-sm outline-none border border-zinc-200 focus:border-[#002FA7] bg-white disabled:opacity-60"
            data-testid="top-picks-add-input"
          />
          <button type="submit" disabled={busy || !draft.trim()} className="text-xs uppercase tracking-[0.1em] px-3 h-8 bg-zinc-900 text-white hover:bg-[#002FA7] disabled:opacity-50 inline-flex items-center gap-1" data-testid="top-picks-add-submit">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Add
          </button>
          <button
            type="button"
            onClick={() => { setDraft(""); setAdding(false); }}
            className="text-xs text-zinc-500 hover:text-zinc-900 px-2 h-8"
            data-testid="top-picks-add-done"
            title="Close add input"
          >
            Done
          </button>
        </form>
      )}

      {/* Picks list */}
      {hasPicks ? (
        <ul className="divide-y divide-zinc-200">
          {picks.map((p) => {
            const linkedTask = p.kind === "task" ? (tasks || []).find((t) => t.id === p.task_id) : null;
            // Title: prefer the live task title; otherwise cached label on the pick;
            // otherwise stash whatever we can. Keeps the row stable across refetches.
            const label = p.kind === "task"
              ? (linkedTask?.title || p.label || "Task")
              : (p.label || "(no label)");
            // Completed: prefer the live task value; fall back to pick.completed
            // (kept in sync via optimistic update in toggleComplete).
            const completed = p.kind === "task"
              ? (linkedTask ? !!linkedTask.completed : !!p.completed)
              : !!p.completed;
            return (
              <li key={p.id} className="flex items-center gap-2 px-4 py-2 group" data-testid={`top-pick-${p.id}`}>
                <span className="text-[10px] font-mono text-zinc-400 w-5 shrink-0">{(p.order || 0) + 1}.</span>
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleComplete(p); }}
                    className={`w-4 h-4 shrink-0 border flex items-center justify-center transition-colors ${completed ? "bg-emerald-600 border-emerald-600" : "border-zinc-400 hover:border-zinc-900"}`}
                    aria-label={completed ? "Mark incomplete" : "Mark complete"}
                    data-testid={`top-pick-check-${p.id}`}
                  >
                    {completed && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </button>
                ) : (
                  <span className={`w-4 h-4 shrink-0 border flex items-center justify-center ${completed ? "bg-emerald-600 border-emerald-600" : "border-zinc-300"}`}>
                    {completed && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </span>
                )}
                {p.kind === "task" ? (
                  <button
                    type="button"
                    onClick={() => linkedTask && onOpenTask?.(linkedTask)}
                    className="flex-1 flex items-center gap-1.5 text-left text-sm text-zinc-800 hover:text-[#002FA7] min-w-0"
                  >
                    <ChevronRight className="w-3 h-3 text-zinc-400 shrink-0" />
                    <span className={`truncate ${completed ? "line-through text-zinc-400" : ""}`}>{label}</span>
                  </button>
                ) : (
                  <div className="flex-1 flex items-center gap-1.5 text-sm text-zinc-700 min-w-0">
                    <span className={`truncate ${completed ? "line-through text-zinc-400" : ""}`}>{label}</span>
                    {!readOnly && !completed && (
                      <button
                        type="button"
                        onClick={() => promote(p.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[9px] uppercase tracking-[0.1em] text-[#002FA7] hover:underline ml-1"
                        title="Convert to task"
                        data-testid={`top-pick-promote-${p.id}`}
                      >
                        Add as task <ArrowUpRight className="w-2.5 h-2.5 inline" />
                      </button>
                    )}
                  </div>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-700 p-1 shrink-0"
                    aria-label="Remove pick"
                    data-testid={`top-pick-remove-${p.id}`}
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : !adding && suggestions.length === 0 ? (
        <p className="px-4 py-3 text-xs text-zinc-500 italic">
          What 3 things matter most here? Pin them up top so they don't get lost.
        </p>
      ) : null}
    </section>
  );
}
